import React, { useState, useCallback, useRef } from 'react';
import { Modal }                    from 'react-bootstrap';
import { Row, Grid }                from 'react-flexbox-grid';
import InteractiveViewerModal       from '../interactiveViewerModal';
import DrawingLinks                 from '../DrawingLinks';
import useOneLineDrawings           from '../../../hooks/useOnelineDrawings';
import { log }                      from '../../../utils/Viewer';
import './attestedDrawingModal.css';

/**
 * AttestedDrawingsModal
 *
 * Wrapper modal for the InteractiveViewerModal (3D viewer with asset selection).
 * Displays OneLine Drawings as clickable links and provides access to the 3D viewer.
 *
 * Modal Configuration:
 * - backdrop="static" : Prevents closing when clicking outside the modal.
 * - keyboard={false}  : Prevents closing with the ESC key.
 * - Users can only close via explicit Save / Cancel buttons in InteractiveViewerModal.
 */
const AttestedDrawingsModal = (props) => {
  const {
    project,
    preEForm,
    onSave,
    onViewerSave,
    saveVisibleText,
    ...rest
  } = props;

  const { drawings, loading: loadingDrawings, error: drawingsError, refetch: refetchDrawings } =
    useOneLineDrawings(project?.id, project?.bpid);

  const [show,                  setShow]                  = useState(false);
  const [selectedDrawingUrn,    setSelectedDrawingUrn]    = useState(null);
  const [selectedAttestationId, setSelectedAttestationId] = useState(null);

  // ── Use a ref so handleClose always reads the current dirty value
  // synchronously. useState is async — setHasUnsavedChanges(false) in
  // handleSaveSuccess schedules a future render; the memoised handleClose
  // closure still sees the old `true` value when setShow() fires immediately
  // after, causing a spurious "unsaved changes" prompt after a successful save.
  const hasUnsavedChangesRef = useRef(false);

  const resetModalState = useCallback(() => {
    hasUnsavedChangesRef.current = false;
    setShow(false);
    setSelectedDrawingUrn(null);
    setSelectedAttestationId(null);
  }, []);

  // ── attestationId is forwarded from DrawingLinks via onOpen so the viewer
  // always knows which Request # belongs to the currently open drawing.
  const handleOpen = useCallback((documentUrn = null, attestationId = null) => {
    const urnString = documentUrn != null ? String(documentUrn) : null;
    if (urnString && !urnString.startsWith('urn:')) {
      console.error('[AttestedDrawingsModal] Invalid URN format:', urnString, `(type: ${typeof documentUrn})`);
      return;
    }
    log('[AttestedDrawingsModal] Opening viewer with URN:', urnString, 'Request #:', attestationId);
    hasUnsavedChangesRef.current = false;   // reset on each new open
    setSelectedDrawingUrn(urnString);
    setSelectedAttestationId(attestationId);
    setShow(true);
  }, []);

  // ── handleClose reads from the ref — always current, never stale.
  // Warning only fires when the user tries to close WITHOUT saving
  // and they have made at least one change (select OR deselect).
  const handleClose = useCallback(() => {
    if (hasUnsavedChangesRef.current && saveVisibleText) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?',
      );
      if (!confirmed) return;
    }
    resetModalState();
  }, [saveVisibleText, resetModalState]);

  const handleCheckboxChange = useCallback(() => {
    hasUnsavedChangesRef.current = true;
  }, []);

  const handleSaveSuccess = useCallback(async (projectId, savedAssetIds = [], updatedMappings) => {
    hasUnsavedChangesRef.current = false;
    await refetchDrawings();
    onViewerSave?.(projectId, savedAssetIds, updatedMappings);
  }, [refetchDrawings, onViewerSave]);

  return (
    <>
      <Row controlId="oneLine" className="one-line-drawings-row">
        <label className="col-xs-12 col-md-2 one-line-drawings-label">
          <strong>OneLine Drawings:</strong>
        </label>
        <Grid fluid className="container drawings-container">
          <DrawingLinks
            drawings={drawings}
            loading={loadingDrawings}
            error={drawingsError}
            onOpen={handleOpen}
          />
        </Grid>
      </Row>

      <Modal
        show={show}
        onHide={handleClose}
        fullscreen={true}
        dialogClassName="modal-fullscreen-custom"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>
          <InteractiveViewerModal
            project={project}
            preEForm={preEForm}
            setShow={handleClose}
            urn={selectedDrawingUrn}
            // attestationId={selectedAttestationId}
            saveVisibleText={saveVisibleText}
            onCheckboxChange={handleCheckboxChange}
            onViewerSave={handleSaveSuccess}
            {...rest}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AttestedDrawingsModal;